import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, StatusBadge } from "@/components/shared/Common";
import { FilesPanel } from "@/components/shared/Records";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Pick } from "@/pages/gerencia/Sales";
import { todayCL } from "@/pages/operaciones/OpsDialogs";

export const SEVERITY = { baja: ["info", "Baja"], media: ["pendiente", "Media"], alta: ["bloqueado", "Alta"], critica: ["bloqueado", "Crítica"] };
export const ACTION_ST = { pendiente: ["pendiente", "Pendiente"], ejecutada: ["info", "Ejecutada · por verificar"], verificada: ["activo", "Verificada"], rechazada: ["bloqueado", "Rechazada"] };

export const usePrevLookups = () => {
  const [l, setL] = useState({ ots: [], users: [] });
  useEffect(() => { api.get("/prevencion/lookups").then((r) => setL(r.data)).catch(() => {}); }, []);
  return l;
};

export const OtFilter = ({ ots, value, onChange }) => (
  <div className="w-full sm:w-80"><Pick testid="prev-ot-filter" value={value || "all"} onChange={(v) => onChange(v === "all" ? "" : v)} options={[{ id: "all", name: "Todas las OT" }, ...ots]} /></div>
);

export const openPdf = async (url) => { try { const r = await api.get(url, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); } catch (e) { toast.error(errMsg(e)); } };

export const NewActionDialog = ({ target, users, onClose, onSaved }) => {
  const [f, setF] = useState({ description: "", responsible_id: "", due_date: "" });
  useEffect(() => { if (target) setF({ description: "", responsible_id: "", due_date: "" }); }, [target]);
  const save = async () => { try { await api.post("/prevencion/actions", { ...target, ...f }); toast.success("Acción creada"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="new-action-dialog">
        <DialogHeader><DialogTitle>Nueva acción correctiva</DialogTitle></DialogHeader>
        <Field label="Acción *"><Textarea data-testid="action-desc-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
        <div className="grid sm:grid-cols-2 gap-3">
          <Field label="Responsable *"><Pick testid="action-resp-select" value={f.responsible_id} onChange={(v) => setF({ ...f, responsible_id: v })} options={users} placeholder="Seleccione" /></Field>
          <Field label="Plazo *"><Input data-testid="action-due-input" type="date" min={todayCL()} value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} /></Field>
        </div>
        <DialogFooter><Button data-testid="action-save-btn" className="rounded-full" onClick={save}>Crear acción</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const ActionDialog = ({ action, onClose, onSaved }) => {
  const { can, user } = useAuth();
  const [note, setNote] = useState("");
  useEffect(() => setNote(""), [action]);
  if (!action) return null;
  const step = async (s) => { try { await api.post(`/prevencion/actions/${action.id}/${s}`, { note }); toast.success("Acción actualizada"); onSaved(); } catch (e) { toast.error(errMsg(e)); } };
  const mine = action.responsible_id === user?.id || can("prevencion.acciones.gestionar");
  const open = ["pendiente", "rechazada"].includes(action.status);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="action-dialog">
        <DialogHeader><DialogTitle>{action.code} · {action.ot_code}</DialogTitle></DialogHeader>
        <p className="text-sm">{action.description}</p>
        <div className="text-xs text-[#5b6656]">Responsable {action.responsible_name} · plazo {fmtDate(action.due_date)} · <StatusBadge status={ACTION_ST[action.status][0]} label={ACTION_ST[action.status][1]} /></div>
        {action.history?.length > 0 && <div className="text-xs space-y-1 border-t pt-2">{action.history.map((h, i) => <div key={i}><b>{h.to}</b> · {h.user_name}: {h.note}</div>)}</div>}
        <FilesPanel resourceType="prev_action" resourceId={action.id} canWrite={open && mine} />
        {(open && mine) || (action.status === "ejecutada" && can("prevencion.acciones.cerrar")) ? <>
          <Field label={open ? "Nota de cierre (qué se hizo) *" : "Comentario de verificación *"}><Textarea data-testid="action-note-input" value={note} onChange={(e) => setNote(e.target.value)} /></Field>
          <DialogFooter className="gap-2 flex-wrap">
            {open && <Button data-testid="action-execute-btn" className="rounded-full" onClick={() => step("execute")}>Informar ejecución</Button>}
            {action.status === "ejecutada" && <><Button data-testid="action-reject-btn" variant="outline" className="rounded-full" onClick={() => step("reject")}>Rechazar</Button>
              <Button data-testid="action-verify-btn" className="rounded-full" onClick={() => step("verify")}>Verificar y cerrar</Button></>}
          </DialogFooter></> : null}
      </DialogContent>
    </Dialog>
  );
};

export const ActionRow = ({ a, onOpen }) => (
  <button data-testid={`action-row-${a.id}`} onClick={() => onOpen(a)} className="w-full flex flex-wrap items-center gap-2 px-4 py-2 text-left text-sm hover:bg-[#f6fbf2] border-t border-[#f0f2ee]">
    <span className="font-mono text-xs">{a.code}</span><span className="flex-1 min-w-0 truncate">{a.description}</span>
    <span className={`text-xs ${a.overdue || (["pendiente", "rechazada"].includes(a.status) && a.due_date < todayCL()) ? "text-[#b42318] font-bold" : "text-[#5b6656]"}`}>{a.responsible_name} · {fmtDate(a.due_date)}</span>
    <StatusBadge status={ACTION_ST[a.status][0]} label={ACTION_ST[a.status][1]} />
  </button>
);
